import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Minus, Plus, Trash2, FileText, Package } from "lucide-react";
import { useQuoteList } from "@/contexts/QuoteListContext";

type QuoteListDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const QuoteListDrawer = ({ open, onClose }: QuoteListDrawerProps) => {
  const navigate = useNavigate();
  const { items, removeFromQuote, updateQuantity, itemCount, clearQuote } = useQuoteList();

  const handleRequestQuote = () => {
    onClose();
    // Navigate to quote page - products will be merged in QuoteRequest
    navigate("/cotizacion");
  };

  const handleQuantityChange = (productId: string, delta: number, currentQuantity: number) => {
    const newQuantity = currentQuantity + delta;
    if (newQuantity <= 0) {
      removeFromQuote(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="flex flex-col w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Mi Cotización
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <Package className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              Tu lista está vacía
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Agrega productos para solicitar cotización
            </p>
            <Button variant="outline" onClick={onClose}>
              Ver Catálogo
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground line-clamp-2">
                        {item.product_name}
                      </h4>
                      {item.category_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.category_name}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.product_id, -1, item.quantity)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.product_id, 1, item.quantity)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => removeFromQuote(item.product_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-border pt-4 mt-auto space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {items.length} producto{items.length !== 1 ? "s" : ""} ({itemCount} unidades)
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={clearQuote}
                >
                  Limpiar todo
                </Button>
              </div>
              
              <Button className="w-full" size="lg" onClick={handleRequestQuote}>
                <FileText className="mr-2 h-5 w-5" />
                Solicitar Cotización
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default QuoteListDrawer;
